import copyTextToClipboard from '../../../common/copyTextToClipboard.js'
import {NEW_ID} from '../../../common/constants/index.js'
import {showMessageToast} from '../../Header/Toast/index.js'
import {setAPIKey} from '../../../data/apiKeys.js'
import {
  formSelector,
  getState,
  onlyIfAutoForm,
  updateForm,
  useSelector,
} from '../../../store.js'
import {isPresent} from '../../../common/utils.js'
import TextInput from '../Common/TextInput.js'
import ButtonPrimary from '../../../common/components/Button/ButtonPrimary.js'
import LinkButton from '../../../common/components/Link/LinkButton.js'
import {buildPath} from '../../../common/querystring.js'
import {API_KEYS, SETTINGS} from '../../../common/constants/SettingsPanels.js'
import api from '../../../common/api.js'
import {getUsers} from '../../../data/users.js'
import {navigate} from '../../../common/location.js'
import {showConfirmMessageModal} from '../../../common/components/Modal/ConfirmMessageModal.js'
import Column from '../Common/Column.js'
import FormList from '../Common/FormList.js'
import FormListItem from '../Common/FormListItem.js'

const API_KEY_FORM_BASE = 'api_key_form_'

function setupReturnOrderCodeForm({apiKey}) {
  const apiKeyID = apiKey?.id || NEW_ID

  return {
    formName: `${API_KEY_FORM_BASE}${apiKeyID}`,
    initialForm: {
      apiKeyID,
      name: apiKey?.name || 'API Key',
      isSaving: false,
      serverError: null,
    },
  }
}

function updateAPIKeyForm(formName, updates) {
  updateForm(formName, updates)
}

function apiKeyFormSelector(state, {formName}) {
  return formSelector(state, {formName})
}

function apiKeyEditFormErrors(state, {formName}) {
  const form = apiKeyFormSelector(state, {formName})
  const errors = {}

  if (!isPresent(form.name)) {
    errors.name = 'Name is required'
    errors.preventSave = true
  }

  return errors
}

export async function saveAPIKey(formName) {
  const {apiKeyID, name} = apiKeyFormSelector(getState(), {formName})

  const params = {
    name,
  }

  updateAPIKeyForm(formName, {serverError: null, isSaving: true})

  try {
    const {json} =
      apiKeyID === NEW_ID
        ? await api.post('/api_key/', params)
        : await api.put(buildPath(['api_key', apiKeyID]), params)

    setAPIKey(json)

    await getUsers()

    if (apiKeyID === NEW_ID) {
      navigate([SETTINGS, API_KEYS, json.id])
    } else {
      navigate([SETTINGS, API_KEYS])
    }
  } catch (err) {
    updateAPIKeyForm(formName, {
      serverError: err.message || err.error_message,
    })
  } finally {
    updateAPIKeyForm(formName, {isSaving: false})
  }
}

export async function archiveAPIKey(formName) {
  const {apiKeyID} = apiKeyFormSelector(getState(), {formName})

  await showConfirmMessageModal({
    title: 'Delete API Key',
    message: 'Do you want to delete this API Key?',
    async onResolve({outcome}) {
      if (!outcome) {
        return
      }

      const {json} = await api.delete(buildPath(['api_key', apiKeyID]))

      navigate([SETTINGS, API_KEYS])

      setAPIKey(json)
    },
  })
}

function Form({apiKey, form, formName}) {
  const errors = useSelector((state) => apiKeyEditFormErrors(state, {formName}))

  const isNew = !apiKey

  return (
    <form
      className="wrap--row margin-top-30"
      onSubmit={(event) => {
        event.preventDefault()

        saveAPIKey(formName)
      }}
    >
      <Column className="medium-8">
        {form.serverError && (
          <div className="row">
            <ul className="medium-8 columns error-list">
              <li>{form.serverError}</li>
            </ul>
          </div>
        )}
        <FormList>
          <FormListItem
            className="form-list-item bigger margin-bottom-20"
            hasError={errors.name}
          >
            <TextInput
              id="api_key_name"
              label="Name"
              required
              value={form.name}
              onChange={(event) =>
                updateAPIKeyForm(formName, {name: event.target.value})
              }
              errorMessage={errors.name}
            />
          </FormListItem>
          {!isNew && (
            <>
              <FormListItem className="form-list-item bigger margin-bottom-20">
                <label className="fs-00 margin-bottom-3" htmlFor="user_name">
                  Client ID
                </label>
                <div className="inline-block">
                  <div className="inline-block lh-sm">{apiKey.client_id}</div>
                  <ButtonPrimary
                    className="fs-n2 margin-left-5"
                    size="x-sm"
                    isOutlined
                    onClick={() => {
                      copyTextToClipboard(apiKey.client_id)

                      showMessageToast('Client ID copied to clipboard')
                    }}
                  >
                    Copy
                  </ButtonPrimary>
                </div>
              </FormListItem>
              <FormListItem className="form-list-item bigger margin-bottom-20">
                <label className="fs-00 margin-bottom-3" htmlFor="user_name">
                  Client Secret
                </label>
                <div className="inline-block">
                  <div className="inline-block lh-sm">
                    {apiKey.client_secret}
                  </div>
                  <ButtonPrimary
                    className="fs-n2 margin-left-5"
                    size="x-sm"
                    isOutlined
                    onClick={() => {
                      copyTextToClipboard(apiKey.client_secret)

                      showMessageToast('Client Secret copied to clipboard')
                    }}
                  >
                    Copy
                  </ButtonPrimary>
                </div>
              </FormListItem>
            </>
          )}
        </FormList>

        <div className="divider--top padding-top-15">
          <ButtonPrimary
            type="submit"
            isLoading={form.isSaving}
            isDisabled={errors.preventSave}
            onClick={() => saveAPIKey(formName)}
          >
            {isNew ? 'Create New API Key' : 'Save'}
          </ButtonPrimary>
          {!isNew && (
            <ButtonPrimary
              className="margin-left-15"
              isOutlined
              onClick={() => archiveAPIKey(formName)}
            >
              Delete
            </ButtonPrimary>
          )}
          <LinkButton
            className="margin-left-15"
            href={'#' + buildPath([SETTINGS, API_KEYS])}
          >
            Cancel
          </LinkButton>
        </div>
      </Column>
    </form>
  )
}

export default onlyIfAutoForm(Form, setupReturnOrderCodeForm)
